import React, { useState } from 'react';
import {
  Copy,
  Check,
  Pin,
  Trash2,
  ExternalLink,
  Code2,
  Palette,
  FileText,
  Image as ImageIcon,
  Search,
  Clock,
  Download,
  Sparkles,
  Maximize2,
  X,
  SlidersHorizontal,
  FileCode,
  Hash,
  Paperclip,
  CheckCheck
} from 'lucide-react';
import { ClipItem, ClipType } from '../types';
import confetti from 'canvas-confetti';

interface ClipboardHistoryProps {
  clips: ClipItem[];
  onCopyClip: (clip: ClipItem) => void;
  onTogglePin: (clipId: string) => void;
  onDeleteClip: (clipId: string) => void;
  onClearAll: () => void;
  onInspectClip: (clip: ClipItem) => void;
  searchRef?: React.RefObject<HTMLInputElement>;
  selectedFilter?: string;
  onSelectFilter?: (filter: string) => void;
}

export const ClipboardHistory: React.FC<ClipboardHistoryProps> = ({
  clips,
  onCopyClip,
  onTogglePin,
  onDeleteClip,
  onClearAll,
  onInspectClip,
  searchRef,
  selectedFilter = 'all',
  onSelectFilter
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [localFilter, setLocalFilter] = useState<string>(selectedFilter);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'newest' | 'pinned'>('pinned');

  const activeFilter = onSelectFilter ? selectedFilter : localFilter;
  const setFilter = (f: string) => {
    if (onSelectFilter) onSelectFilter(f);
    else setLocalFilter(f);
  };

  const triggerHaptic = () => {
    try {
      if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
        navigator.vibrate(15);
      }
    } catch {}
  };

  const handleCopy = (clip: ClipItem, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    triggerHaptic();
    navigator.clipboard.writeText(clip.content);
    setCopiedId(clip.id);
    onCopyClip(clip);

    try {
      confetti({
        particleCount: 18,
        spread: 40,
        origin: { y: 0.85 },
        colors: ['#00F0FF', '#7000FF', '#FFFFFF']
      });
    } catch {}

    setTimeout(() => {
      setCopiedId(null);
    }, 1800);
  };

  const getClipIcon = (type: ClipType) => {
    switch (type) {
      case 'url':
        return <ExternalLink size={13} className="text-cyan-400" />;
      case 'code':
        return <Code2 size={13} className="text-purple-400" />;
      case 'color':
        return <Palette size={13} className="text-pink-400" />;
      case 'image':
        return <ImageIcon size={13} className="text-emerald-400" />;
      case 'file':
        return <Paperclip size={13} className="text-amber-400" />;
      default:
        return <FileText size={13} className="text-slate-400" />;
    }
  };

  const formatTimestamp = (ts: number) => {
    const diff = Math.floor((Date.now() - ts) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatBytes = (bytes: number) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Filter and sort clips
  const filteredClips = clips
    .filter((clip) => {
      // Type filter
      if (activeFilter === 'pinned' && !clip.isPinned) return false;
      if (activeFilter !== 'all' && activeFilter !== 'pinned' && clip.metadata.type !== activeFilter) return false;

      // Text search
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return (
        clip.content.toLowerCase().includes(q) ||
        clip.metadata.senderDeviceName.toLowerCase().includes(q) ||
        (clip.metadata.fileName && clip.metadata.fileName.toLowerCase().includes(q))
      );
    })
    .sort((a, b) => {
      if (sortBy === 'pinned') {
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
      }
      return b.metadata.timestamp - a.metadata.timestamp;
    });

  const filterTabs = [
    { id: 'all', label: 'All Items', count: clips.length },
    { id: 'pinned', label: 'Pinned', count: clips.filter((c) => c.isPinned).length },
    { id: 'code', label: 'Code', count: clips.filter((c) => c.metadata.type === 'code').length },
    { id: 'url', label: 'URLs', count: clips.filter((c) => c.metadata.type === 'url').length },
    { id: 'image', label: 'Media', count: clips.filter((c) => c.metadata.type === 'image').length },
    { id: 'file', label: 'Files', count: clips.filter((c) => c.metadata.type === 'file').length }
  ];

  return (
    <div className="space-y-4">
      {/* Stream Controls: Search & Category Tabs */}
      <div className="native-glass rounded-2xl p-3 md:p-3.5 border border-white/10 shadow-lg">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
          {/* Search Bar */}
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              ref={searchRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search clips by text, code, device name... (/ to focus)"
              className="glass-input w-full pl-8 pr-8 py-2 text-xs"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white p-0.5"
              >
                <X size={13} />
              </button>
            )}
          </div>

          {/* Right Action: Clear All */}
          {clips.length > 0 && (
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={onClearAll}
                className="text-xs text-slate-400 hover:text-rose-400 hover:bg-rose-950/30 px-2.5 py-1.5 rounded-xl border border-white/5 hover:border-rose-500/30 flex items-center gap-1.5 transition-all cursor-pointer"
                title="Clear local clipboard history"
              >
                <Trash2 size={13} />
                <span>Clear All</span>
              </button>
            </div>
          )}
        </div>

        {/* Filter Pills */}
        <div className="flex items-center gap-1.5 overflow-x-auto pt-3 border-t border-white/5 mt-2.5 no-scrollbar">
          {filterTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              className={`px-3 py-1 rounded-xl text-xs font-semibold whitespace-nowrap transition-all flex items-center gap-1.5 cursor-pointer ${
                activeFilter === tab.id
                  ? 'bg-cyan-500/15 text-cyan-300 border border-cyan-500/40 shadow-sm'
                  : 'text-slate-400 hover:text-white hover:bg-white/5 border border-transparent'
              }`}
            >
              <span>{tab.label}</span>
              {tab.count > 0 && (
                <span className={`text-[10px] font-mono px-1.5 py-0.2 rounded-full ${
                  activeFilter === tab.id ? 'bg-cyan-400 text-black font-bold' : 'bg-white/10 text-slate-400'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Clips List */}
      {filteredClips.length === 0 ? (
        <div className="native-glass rounded-2xl p-10 border border-white/10 text-center shadow-xl">
          <div className="w-14 h-14 rounded-2xl bg-cyan-950/40 border border-cyan-500/20 flex items-center justify-center mx-auto mb-3.5 text-cyan-400 shadow-inner">
            <Sparkles size={24} className="animate-pulse" />
          </div>
          <h3 className="text-sm font-bold text-white font-display mb-1">
            {searchQuery ? 'No matching clips found' : 'Ready for Instant Sync'}
          </h3>
          <p className="text-xs text-slate-400 max-w-sm mx-auto leading-relaxed">
            {searchQuery
              ? `No clips matched "${searchQuery}". Try a different search keyword or filter.`
              : 'Copy anything on your Android phone, Windows PC, or Mac. It will appear here instantly with sub-20ms delivery.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredClips.map((clip) => {
            const isCopied = copiedId === clip.id;
            const isImage = clip.metadata.type === 'image';
            const isCode = clip.metadata.type === 'code';
            const isUrl = clip.metadata.type === 'url';
            const isColor = clip.metadata.type === 'color';
            const isFile = clip.metadata.type === 'file';

            return (
              <div
                key={clip.id}
                onClick={() => onInspectClip(clip)}
                className={`glass-card p-3.5 md:p-4 transition-all relative group cursor-pointer border ${
                  clip.isPinned
                    ? 'border-cyan-500/40 bg-gradient-to-r from-cyan-950/20 via-slate-900/40 to-purple-950/20 shadow-glow-cyan'
                    : 'border-white/10 hover:border-white/20'
                }`}
              >
                {/* Header: Sender Info & Tag & Actions */}
                <div className="flex items-center justify-between gap-2 mb-2.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-6 h-6 rounded-lg bg-black/50 border border-white/10 flex items-center justify-center flex-shrink-0">
                      {getClipIcon(clip.metadata.type)}
                    </span>

                    <span className="text-xs font-bold text-slate-200 truncate">
                      {clip.metadata.senderDeviceName}
                    </span>

                    <span className="text-[10px] text-slate-500 font-mono hidden sm:inline">
                      • {formatTimestamp(clip.metadata.timestamp)}
                    </span>

                    {clip.isLocal && (
                      <span className="text-[9px] font-bold uppercase tracking-wider bg-white/5 text-slate-400 border border-white/10 px-1.5 py-0.2 rounded">
                        Local
                      </span>
                    )}

                    {clip.isPinned && (
                      <span className="text-[9px] font-bold uppercase tracking-wider bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 px-1.5 py-0.2 rounded flex items-center gap-1">
                        <Pin size={8} className="fill-current" />
                        Pinned
                      </span>
                    )}
                  </div>

                  {/* Top Right Quick Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => onTogglePin(clip.id)}
                      className={`p-1.5 rounded-lg transition-colors ${
                        clip.isPinned
                          ? 'text-cyan-400 bg-cyan-500/10'
                          : 'text-slate-400 hover:text-white hover:bg-white/5'
                      }`}
                      title={clip.isPinned ? 'Unpin' : 'Pin to top'}
                    >
                      <Pin size={13} className={clip.isPinned ? 'fill-current' : ''} />
                    </button>

                    <button
                      onClick={(e) => handleCopy(clip, e)}
                      className={`px-2.5 py-1 text-xs font-bold rounded-lg border transition-all flex items-center gap-1.5 cursor-pointer ${
                        isCopied
                          ? 'bg-emerald-500 text-black border-emerald-400 font-extrabold shadow-glow-emerald'
                          : 'bg-white/5 text-cyan-300 hover:bg-cyan-500/20 border-white/10 hover:border-cyan-500/40'
                      }`}
                      title="Copy to clipboard"
                    >
                      {isCopied ? <Check size={12} /> : <Copy size={12} />}
                      <span>{isCopied ? 'Copied' : 'Copy'}</span>
                    </button>

                    <button
                      onClick={() => onDeleteClip(clip.id)}
                      className="p-1.5 text-slate-500 hover:text-rose-400 rounded-lg hover:bg-rose-950/30 transition-colors"
                      title="Delete clip"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>

                {/* Body Content Rendering */}
                {isImage ? (
                  <div className="rounded-xl overflow-hidden bg-black/40 border border-white/5 max-h-60 flex items-center justify-center relative">
                    <img
                      src={clip.content}
                      alt="Synced clip"
                      className="max-h-60 w-auto object-contain"
                      loading="lazy"
                    />
                  </div>
                ) : isCode ? (
                  <div className="rounded-xl bg-[#080A14] border border-white/5 p-3 font-mono text-xs text-cyan-200 overflow-x-auto max-h-48 whitespace-pre leading-relaxed">
                    {clip.content}
                  </div>
                ) : isColor ? (
                  <div className="flex items-center gap-3 p-2.5 rounded-xl bg-black/40 border border-white/5">
                    <div
                      className="w-8 h-8 rounded-lg shadow-inner border border-white/20 flex-shrink-0"
                      style={{ backgroundColor: clip.content }}
                    ></div>
                    <div className="font-mono text-xs font-bold text-white">
                      {clip.content}
                    </div>
                  </div>
                ) : isUrl ? (
                  <div className="flex items-center justify-between gap-2 p-2.5 rounded-xl bg-cyan-950/20 border border-cyan-500/20">
                    <span className="font-mono text-xs text-cyan-300 truncate">{clip.content}</span>
                    <a
                      href={clip.content}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="p-1.5 text-cyan-400 hover:text-white rounded-lg hover:bg-cyan-500/20 transition-colors flex-shrink-0"
                      title="Open URL in new browser tab"
                    >
                      <ExternalLink size={14} />
                    </a>
                  </div>
                ) : (
                  <p className="text-xs md:text-sm text-slate-200 line-clamp-4 leading-relaxed font-sans break-words">
                    {clip.content}
                  </p>
                )}

                {/* Footer Metadata */}
                <div className="flex items-center justify-between text-[10px] text-slate-500 font-mono mt-2.5 pt-2 border-t border-white/5">
                  <span>{formatBytes(clip.metadata.sizeBytes)}</span>
                  <span>Click card to inspect</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
